"use client";

// Sentry client init — must be in a rendered "use client" module (side-effect imports
// from Server Components are tree-shaken by Next.js/Turbopack)
import "../../sentry.client.config";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

if (
  typeof window !== "undefined" &&
  process.env.NEXT_PUBLIC_POSTHOG_KEY
) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST || "/ingest",
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    person_profiles: "always",
    capture_pageview: false, // We capture manually below
    capture_pageleave: true,
    autocapture: false,
    capture_exceptions: true,
    // Session recording disabled 2026-07-01: the /s ingest upstream on
    // ph.mint24.app has been 502ing since ~06-27 (recordings weren't saving),
    // while posthog-js kept uploading multi-MB snapshots (up to 25MB) that
    // saturated mobile uplinks and starved the browser connection pool →
    // 2-minute page loads for RU mobile users. Re-enable with sampling once
    // the replay-capture backend is fixed.
    disable_session_recording: true,
    disable_compression: true,
    advanced_disable_feature_flags: true,
    advanced_disable_feature_flags_on_first_load: true,
    capture_performance: { web_vitals: true },
  });

  const ua = navigator.userAgent || "";

  // Track "slow_load" when LCP/FCP exceeds threshold — lets us alert on >5% of sessions
  try {
    const SLOW_LCP_MS = 4000;
    const SLOW_FCP_MS = 3000;
    let reportedSlow = false;

    const reportSlow = (metric: "lcp" | "fcp", value: number) => {
      if (reportedSlow) return;
      reportedSlow = true;
      const browserMatch = ua.match(/(Chrome|Safari|Firefox|Edg|OPR|YaBrowser)\/[\d.]+/i);
      const browser = browserMatch ? browserMatch[0] : "unknown";
      const duration_ms = Math.round(value);
      const connection =
        (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
          ?.effectiveType ?? "unknown";

      // PostHog only — do NOT spam Sentry Issues (was MINT-AI-WEB-6N/6K top
      // noise: every flaky mobile session = warning issue). Track % slow
      // sessions in PH dashboards / alerts instead.
      posthog.capture("slow_load", {
        metric,
        duration_ms,
        url: location.pathname,
        browser,
        user_agent: ua,
        connection,
      });
    };

    if ("PerformanceObserver" in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1];
          if (last && last.startTime > SLOW_LCP_MS) reportSlow("lcp", last.startTime);
        });
        lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
      } catch {}

      try {
        const fcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === "first-contentful-paint" && entry.startTime > SLOW_FCP_MS) {
              reportSlow("fcp", entry.startTime);
            }
          }
        });
        fcpObserver.observe({ type: "paint", buffered: true });
      } catch {}
    }
  } catch {}

  // Detect in-app browsers and set as person + event properties
  let inAppBrowser: string | null = null;
  if (/Instagram/i.test(ua)) inAppBrowser = "Instagram";
  else if (/Threads/i.test(ua)) inAppBrowser = "Threads";
  else if (/FBAN|FBAV|FB_IAB/i.test(ua)) inAppBrowser = "Facebook";
  else if (/TelegramBot|Telegram/i.test(ua)) inAppBrowser = "Telegram";

  // Device fingerprint for cross-browser matching (in-app → external)
  const deviceFingerprint = [
    screen.width,
    screen.height,
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency || 0,
  ].join("|");

  posthog.register({
    in_app_browser: inAppBrowser ?? "none",
    is_in_app_browser: inAppBrowser !== null,
    device_fingerprint: deviceFingerprint,
  });

  if (inAppBrowser) {
    posthog.people.set({
      in_app_browser: inAppBrowser,
      is_in_app_browser: true,
      device_fingerprint: deviceFingerprint,
    });
  }
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ph = usePostHog();

  useEffect(() => {
    if (pathname && ph) {
      let url = window.origin + pathname;
      const search = searchParams.toString();
      if (search) {
        url += "?" + search;
      }
      ph.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, ph]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}
